import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, Mail, Lock, User, ArrowRight, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.7 3.9-5.5 3.9-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.9 1.5l2.6-2.6C16.9 3.2 14.7 2.2 12 2.2 6.6 2.2 2.2 6.6 2.2 12s4.4 9.8 9.8 9.8c5.7 0 9.4-4 9.4-9.6 0-.6-.1-1.1-.2-1.6H12z"/></svg>
);

const Login = () => {
  const { t, lang } = useApp();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const next = location.state?.next || params.get('next') || '/chat';
  const [mode, setMode] = useState(params.get('mode') === 'register' ? 'register' : 'login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const a = t.auth;

  useEffect(() => { if (user) navigate(next, { replace: true, state: location.state }); }, [user]); // eslint-disable-line
  if (user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === 'login' ? await api.login({ email: form.email, password: form.password }) : await api.register(form);
      setUser(u);
      toast.success(mode === 'login' ? a.welcomeBack : a.accountCreated);
      navigate(next, { replace: true, state: location.state });
    } catch (err) {
      toast.error(err.message);
    } finally { setBusy(false); }
  };

  const google = () => {
    localStorage.setItem('aurra_next', next);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/chat';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="login-page">
      <div className="hidden lg:flex w-[45%] bg-primary relative overflow-hidden flex-col justify-between p-12 text-primary-foreground">
        <div className="absolute inset-0 aurra-grid-bg opacity-10" />
        <Link to="/" className="relative flex items-center gap-2"><span className="h-9 w-9 rounded-xl bg-white/15 flex items-center justify-center"><Sparkles className="h-5 w-5" /></span><span className="font-display text-xl font-bold">Aurra.</span></Link>
        <div className="relative">
          <h2 className="font-display text-4xl font-bold leading-tight">{a.heroTitle}</h2>
          <p className="mt-4 text-primary-foreground/80 text-lg max-w-md">{a.heroSub}</p>
        </div>
        <p className="relative text-sm text-primary-foreground/60">© {new Date().getFullYear()} Aurra AI</p>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm animate-fade-in">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8"><span className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center"><Sparkles className="h-5 w-5 text-primary-foreground" /></span><span className="font-display text-xl font-bold">Aurra.</span></Link>
          <h1 className="font-display text-3xl font-bold">{mode === 'login' ? a.loginTitle : a.registerTitle}</h1>
          <p className="mt-2 text-muted-foreground">{mode === 'login' ? a.loginSub : a.registerSub}</p>

          <Button type="button" variant="outline" onClick={google} data-testid="google-login-btn" className="mt-8 w-full h-11 rounded-xl font-semibold gap-2"><GoogleIcon /> {a.google}</Button>
          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />{a.or}<span className="h-px flex-1 bg-border" /></div>

          <form onSubmit={submit} className="space-y-4">
            {mode === 'register' && (
              <div className="space-y-1.5">
                <Label>{a.name}</Label>
                <div className="relative"><User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input data-testid="register-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={a.namePh} className="pl-9 rounded-xl h-11" /></div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{a.email}</Label>
              <div className="relative"><Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input data-testid="auth-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="vous@exemple.com" className="pl-9 rounded-xl h-11" /></div>
            </div>
            <div className="space-y-1.5">
              <Label>{a.password}</Label>
              <div className="relative"><Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input data-testid="auth-password-input" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="pl-9 rounded-xl h-11" /></div>
              {mode === 'register' && <p className="text-xs text-muted-foreground">{a.passwordHint}</p>}
              {mode === 'login' && <p className="text-xs text-right"><Link to="/forgot-password" data-testid="forgot-password-link" className="text-primary hover:underline">{a.forgotLink}</Link></p>}
            </div>
            <Button type="submit" disabled={busy} data-testid="auth-submit-btn" className="w-full h-11 rounded-xl font-semibold gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{mode === 'login' ? a.loginBtn : a.registerBtn} <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>

          <p className="mt-6 text-sm text-center text-muted-foreground">
            {mode === 'login' ? a.noAccount : a.hasAccount}{' '}
            <button type="button" data-testid="auth-switch-mode" onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="font-semibold text-primary hover:underline">{mode === 'login' ? a.registerLink : a.loginLink}</button>
          </p>
          <p className="mt-8 text-xs text-center text-muted-foreground">{lang === 'fr' ? 'Gratuit : 15 messages / jour · Pro : illimité' : 'Free: 15 messages / day · Pro: unlimited'}</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
